// @ngf/calc-engine — pure, dependency-free scoring for NGF physicals.
// Every stored result records ENGINE_VERSION; bump it whenever any output can change.
import { scoreAerobic, type AerobicInput, type AerobicResult } from './aerobic.ts';
import { scoreBodyComposition, type BodyCompResult, type Skinfolds } from './bodyComposition.ts';
import { scoreCoronaryRisk, type CoronaryRiskResult, type Smoking } from './coronaryRisk.ts';
import { scoreSpirometry, type SpirometryResult } from './spirometry.ts';
import { round } from './num.ts';
import type { CycleStage, ProvisionalRule, Sex } from './types.ts';

export const ENGINE_VERSION = '0.2.0';   // 0.2.0: cholesterol/HDL ratio, body-fat goals

export * from './types.ts';
export * from './age.ts';
export * as aerobic from './aerobic.ts';
export * as spirometry from './spirometry.ts';
export * as bodyComposition from './bodyComposition.ts';
export * as coronaryRisk from './coronaryRisk.ts';
export * from './checks.ts';   // value checks: flags only, never change a score (no engine version bump)

/** Raw page-1 values needed for scoring. Missing sections are skipped, not errors. */
export interface AssessmentInput {
  sex: Sex;
  age: number;
  heightIn?: number | null;
  weightLb?: number | null;
  skinfolds?: Skinfolds | null;
  sbp?: number | null;
  dbp?: number | null;
  totalChol?: number | null;
  hdl?: number | null;
  smoking?: Smoking | null;
  restHr?: number | null;
  stages?: readonly CycleStage[] | null;
  thrPct?: number;
  thrMethod?: AerobicInput['thrMethod'];
  fvc?: number | null;
  fev1?: number | null;
}

export interface AssessmentResult {
  engineVersion: string;
  aerobic: AerobicResult | null;
  bodyComposition: BodyCompResult | null;
  coronaryRisk: CoronaryRiskResult | null;
  spirometry: SpirometryResult | null;
  /** Total cholesterol ÷ HDL, 0.1 (legacy nCholRatio: 99.55 % of 2023+ rows). Absent before engine 0.2.0. */
  cholHdlRatio?: number | null;
  provisional: ProvisionalRule[];
}

const has = (...xs: unknown[]) => xs.every((x) => typeof x === 'number' && Number.isFinite(x) && x > 0);

export function scoreAssessment(x: AssessmentInput): AssessmentResult {
  const aerobicRes =
    has(x.weightLb) && x.stages?.length
      ? scoreAerobic({
          sex: x.sex, age: x.age, weightLb: x.weightLb!, restHr: x.restHr ?? null,
          stages: x.stages, thrPct: x.thrPct, thrMethod: x.thrMethod,
        })
      : null;
  const body =
    has(x.weightLb) && x.skinfolds
      ? scoreBodyComposition({ sex: x.sex, age: x.age, weightLb: x.weightLb!, skinfolds: x.skinfolds })
      : null;
  const risk =
    has(x.sbp, x.dbp, x.totalChol, x.hdl) && x.smoking
      ? scoreCoronaryRisk({
          sex: x.sex, age: x.age, sbp: x.sbp!, dbp: x.dbp!, totalChol: x.totalChol!, hdl: x.hdl!,
          smoking: x.smoking,
        })
      : null;
  const spiro =
    has(x.heightIn, x.fvc, x.fev1)
      ? scoreSpirometry({ sex: x.sex, age: x.age, heightIn: x.heightIn!, fvc: x.fvc!, fev1: x.fev1! })
      : null;
  const provisional = [...new Set([...(aerobicRes?.provisional ?? []), ...(body?.provisional ?? [])])];
  return {
    engineVersion: ENGINE_VERSION,
    aerobic: aerobicRes,
    bodyComposition: body,
    coronaryRisk: risk,
    spirometry: spiro,
    cholHdlRatio: has(x.totalChol, x.hdl) ? round(x.totalChol! / x.hdl!, 1) : null,
    provisional,
  };
}
